import React from 'react'

import companyIconUrl from '@/assets/img/icons/company.png'
import CompanyTypeIcon from '@/components/misc/CompanyIcon/CompanyTypeIcon'
import Image from '@/components/misc/Image'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { COMPANY_TYPE_CODE } from '@/lib/constants'
import { truncateText } from '@/lib/helpers/helperFunctions'
import { cn } from '@/lib/utils'
import { CompanyTypeCode } from '@/types/misc'

export interface CompanyLogoOrNameProps {
  logoUrl?: string | null
  name: string
  companyTypeCode?: CompanyTypeCode // TODO: Make this required
  showLogoFallback?: boolean
  showNameFallback?: boolean
  wrapperProps?: React.HTMLProps<HTMLDivElement>
  fallbackProps?: React.HTMLProps<HTMLDivElement>
  imageStyle?: React.CSSProperties
  imageClassName?: string
  companyTypeIconStyle?: React.CSSProperties
  truncateName?: boolean
  truncateThreshold?: number
  tooltipContentProps?: Omit<
    React.ComponentProps<typeof TooltipContent>,
    'children'
  >
  title?: string
}

const CompanyLogoOrName: React.FC<CompanyLogoOrNameProps> = ({
  logoUrl,
  name,
  companyTypeCode = COMPANY_TYPE_CODE.other,
  showLogoFallback = true, // TODO: Default this to false
  showNameFallback = true,
  wrapperProps,
  fallbackProps,
  imageClassName,
  imageStyle,
  companyTypeIconStyle,
  truncateName = true,
  truncateThreshold = 60,
  tooltipContentProps,
}) => {
  const companyIconAndNameElement = (
    <div
      {...fallbackProps}
      className={cn('flex items-center gap-2', fallbackProps?.className)}
    >
      {showLogoFallback && (
        <CompanyTypeIcon
          companyTypeCode={companyTypeCode}
          src={companyIconUrl}
          style={{ maxHeight: '20px', ...companyTypeIconStyle }}
          alt={name}
          className="max-w-full"
        />
      )}

      {showNameFallback && (
        <div>
          {truncateName
            ? truncateText(name, { maxLength: truncateThreshold })
            : name}
        </div>
      )}
    </div>
  )

  return (
    <div {...wrapperProps}>
      {logoUrl ? (
        <Tooltip>
          {/* Need the div wrapper for the tooltip to render properly */}
          <TooltipTrigger asChild>
            <Image
              src={logoUrl}
              fallback={companyIconAndNameElement}
              // Don't apply any default styles here as this component is used
              // in a lot of places so it's difficult to get the styles correct
              // for all those use cases. Instead, use the `imageStyle` prop
              // to control the image style where needed.
              style={imageStyle}
              alt={name}
              className={cn('max-w-full object-contain', imageClassName)}
            />
          </TooltipTrigger>

          <TooltipContent {...tooltipContentProps}>{name}</TooltipContent>
        </Tooltip>
      ) : (
        companyIconAndNameElement
      )}
    </div>
  )
}

export default CompanyLogoOrName
